import { useRef, useState } from "react";
import { Sparkles, Mic, Square, Loader2, Copy, Save, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, apiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const KINDS = [
  { id: "post", label: "Articolo del blog" },
  { id: "product", label: "Descrizione prodotto" },
  { id: "work", label: "Racconto lavoro (portfolio)" },
  { id: "free", label: "Testo libero" },
];

const MODELS = [
  { id: "openai", label: "ChatGPT · GPT-5.6 Terra" },
  { id: "anthropic", label: "Claude · Sonnet 5.5" },
];

export default function AiAssistant() {
  const [kind, setKind] = useState("post");
  const [provider, setProvider] = useState("openai");
  const [topic, setTopic] = useState("");
  const [output, setOutput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [imgPrompt, setImgPrompt] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [imgLoading, setImgLoading] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Scrivi o detta prima un argomento"); return; }
    setOutput("");
    setGenerating(true);
    try {
      const res = await fetch(`${API}/admin/ai/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, kind, topic }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Generazione fallita");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") break;
          try {
            const obj = JSON.parse(payload);
            if (obj.error) throw new Error(obj.error);
            if (obj.t) setOutput((o) => o + obj.t);
          } catch (e) {
            if (e.message && !e.message.includes("JSON")) throw e;
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "Generazione fallita");
    } finally {
      setGenerating(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voce.webm");
          const { data } = await api.post("/admin/ai/transcribe", fd);
          setTopic((t) => (t ? t + " " : "") + data.text);
          toast.success("Dettato trascritto");
        } catch (e) {
          toast.error(apiError(e, "Trascrizione fallita"));
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microfono non disponibile. Controlla i permessi del browser.");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const saveDraft = async () => {
    try {
      const lines = output.split("\n").filter(Boolean);
      await api.post("/posts", {
        title: topic.slice(0, 90) || "Bozza AI",
        excerpt: (lines[0] || "").slice(0, 160),
        content: output,
        category: "Bozza AI",
        image: "",
        published: false,
      });
      toast.success("Bozza salvata: la trovi in Articoli");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copy = async (text, msg = "Copiato negli appunti") => {
    await navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  const genImage = async () => {
    if (!imgPrompt.trim()) { toast.error("Descrivi l'immagine da generare"); return; }
    setImgLoading(true);
    setImgUrl("");
    try {
      const { data } = await api.post("/admin/ai/image", { prompt: imgPrompt });
      setImgUrl(data.url);
      toast.success("Immagine generata e salvata");
    } catch (e) {
      toast.error(apiError(e, "Generazione immagine fallita"));
    } finally {
      setImgLoading(false);
    }
  };

  const field = "border-line bg-sand/70 text-forest";
  const btnPrimary = "flex items-center justify-center gap-2 rounded-full bg-forest px-6 py-3.5 font-accent text-xs uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60";

  return (
    <section data-testid="ai-assistant">
      <h2 className="font-serif text-3xl text-forest">Assistente AI</h2>
      <p className="mt-2 text-sm text-sage">Scrivi bozze con ChatGPT o Claude, detta al microfono (Whisper), genera immagini con Gemini Nano Banana.</p>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-sm border border-line bg-bone p-6 md:p-8" data-testid="ai-text-card">
          <p className="flex items-center gap-2 font-accent text-xs uppercase tracking-[0.22em] text-terra">
            <Sparkles className="h-4 w-4" strokeWidth={1.5} /> Scrivi con l'AI
          </p>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Tipo di testo</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className={`mt-2 ${field}`} data-testid="ai-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Modello</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className={`mt-2 ${field}`} data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5">
            <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Argomento</Label>
            <div className="mt-2 flex items-start gap-3">
              <Textarea
                rows={4}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Es. Come preparare il giardino all'estate in Sardegna…"
                className={`${field} flex-1`}
                data-testid="ai-topic-input"
              />
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                data-testid="ai-mic-button"
                aria-label={recording ? "Ferma dettatura" : "Detta al microfono"}
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                  recording ? "animate-pulse border-destructive bg-destructive text-bone" : "border-line text-forest hover:bg-sand"
                } disabled:opacity-60`}
              >
                {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" strokeWidth={1.5} />}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-sage">Premi il microfono e detta: Whisper trascrive in italiano.</p>
          </div>
          <button onClick={generate} disabled={generating} className={`mt-6 w-full ${btnPrimary}`} data-testid="ai-generate-button">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" strokeWidth={1.5} />}
            {generating ? "Sto scrivendo…" : "Genera testo"}
          </button>
          {output && (
            <div className="mt-6" data-testid="ai-output-wrap">
              <div className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-sm border border-line bg-sand/50 p-5 text-sm leading-relaxed text-forest/85" data-testid="ai-output">
                {output}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button onClick={() => copy(output)} className="flex items-center gap-2 rounded-full border border-line px-5 py-2.5 font-accent text-xs uppercase tracking-[0.18em] text-forest hover:bg-sand" data-testid="ai-copy-button">
                  <Copy className="h-3.5 w-3.5" /> Copia
                </button>
                {kind === "post" && (
                  <button onClick={saveDraft} className="flex items-center gap-2 rounded-full border border-line px-5 py-2.5 font-accent text-xs uppercase tracking-[0.18em] text-forest hover:bg-sand" data-testid="ai-save-draft-button">
                    <Save className="h-3.5 w-3.5" /> Salva come bozza
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-sm border border-line bg-bone p-6 md:p-8" data-testid="ai-image-card">
          <p className="flex items-center gap-2 font-accent text-xs uppercase tracking-[0.22em] text-terra">
            <Wand2 className="h-4 w-4" strokeWidth={1.5} /> Genera immagini · Gemini Nano Banana
          </p>
          <div className="mt-6">
            <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Descrivi l'immagine</Label>
            <Textarea
              rows={3}
              value={imgPrompt}
              onChange={(e) => setImgPrompt(e.target.value)}
              placeholder="Es. Un olivo secolare al tramonto in un giardino mediterraneo, luce dorata…"
              className={`mt-2 ${field}`}
              data-testid="ai-image-prompt"
            />
          </div>
          <button onClick={genImage} disabled={imgLoading} className={`mt-5 w-full ${btnPrimary}`} data-testid="ai-image-generate-button">
            {imgLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" strokeWidth={1.5} />}
            {imgLoading ? "Generazione in corso…" : "Genera immagine"}
          </button>
          {imgUrl && (
            <div className="mt-6" data-testid="ai-image-result">
              <div className="arch-frame overflow-hidden bg-sand">
                <img src={imgUrl} alt="Immagine generata con AI" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <code className="flex-1 truncate rounded-sm border border-line bg-sand/70 px-3 py-2 text-xs text-sage" data-testid="ai-image-url">{imgUrl}</code>
                <button onClick={() => copy(imgUrl, "URL copiato: incollalo nel campo immagine")} className="flex items-center gap-2 rounded-full border border-line px-4 py-2 font-accent text-xs uppercase tracking-[0.18em] text-forest hover:bg-sand" data-testid="ai-image-copy-button">
                  <Copy className="h-3.5 w-3.5" /> Copia URL
                </button>
              </div>
              <p className="mt-2 text-xs text-sage">L'immagine è salvata nel sito: incolla l'URL nel campo immagine di articoli, prodotti o lavori.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
